import CKEditorMaster from '@/components/CKEditor/CKEditorMaster';
import CustomLabel from '@/components/custom-label';
import {
  getReceiverOptions,
  convertTargetGroupsToOptions
} from '@/constants/notification.constant';
import { NotificationPermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { watchUser } from '@/redux/slices/auth.slice';
import {
  createEmailNotification,
  getEmailNotificationDetail,
  resetNotificationDetail,
  updateEmailNotification,
  watchDetailLoading,
  watchLoadingNotification,
  watchEmailNotificationDetail,
  getTargetGroups,
  watchTargetGroups
} from '@/redux/slices/notification.slice';
import {
  ArrowLeftOutlined,
  CaretRightOutlined,
  CloseCircleOutlined,
  InfoCircleFilled
} from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  App,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

export default function NotificationCreateEmail() {
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [targetGroupValue, setTargetGroupValue] = useState<number[]>([]);
  const [editorKey, setEditorKey] = useState<number>(0);
  const user = useAppSelector(watchUser);

  const dispatch = useAppDispatch();
  const { mode, id } = useQuery();

  const emailNotificationDetail = useAppSelector(watchEmailNotificationDetail);
  const loading = useAppSelector(watchDetailLoading);
  const loadingNotification = useAppSelector(watchLoadingNotification);

  const isEditMode = mode === NotificationPermissionMode.EDITEMAIL && id;
  const notificationId = id ? Number(id) : null;

  const disableSendDateTime = Boolean(
    isEditMode &&
      emailNotificationDetail?.STATUS === 'PENDING' &&
      emailNotificationDetail?.SEND_DATE &&
      dayjs(emailNotificationDetail.SEND_DATE).isBefore(dayjs())
  );

  const targetGroups = useAppSelector(watchTargetGroups);


  const targetGroupOptions = convertTargetGroupsToOptions(targetGroups);
  const receiverOptions = getReceiverOptions();

  const normalizeEmailHtml = (html: string) => {
    if (!html) return html;

    const MAX_EMAIL_WIDTH = 700;

    const measureTableWidths = (rawHtml: string) => {
      if (typeof document === 'undefined') return [] as number[];
      const temp = document.createElement('div');
      temp.className = 'ck-content';
      temp.style.cssText =
        'position:absolute;left:-9999px;top:0;visibility:hidden;width:1000px;max-width:none;';
      temp.innerHTML = rawHtml;
      document.body.appendChild(temp);
      const widths = Array.from(temp.querySelectorAll('table')).map((t) =>
        Math.round(t.getBoundingClientRect().width)
      );
      document.body.removeChild(temp);
      return widths;
    };

    const measureImageWidths = (rawHtml: string) => {
      if (typeof document === 'undefined') return [] as number[];
      const temp = document.createElement('div');
      temp.className = 'ck-content';
      temp.style.cssText =
        'position:absolute;left:-9999px;top:0;visibility:hidden;width:1000px;max-width:none;';
      temp.innerHTML = rawHtml;
      document.body.appendChild(temp);
      const widths = Array.from(temp.querySelectorAll('img')).map((img) =>
        Math.round(img.getBoundingClientRect().width)
      );
      document.body.removeChild(temp);
      return widths;
    };

    const measuredTableWidths = measureTableWidths(html);
    const measuredImageWidths = measureImageWidths(html);

    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Unwrap <figure> to keep content (email clients often strip <figure>)
    const extractStyleValue = (style: string | null, key: string) => {
      if (!style) return null;
      const match = new RegExp(`${key}\\s*:\\s*([^;]+)`, 'i').exec(style);
      return match ? match[1].trim() : null;
    };

    const applyWidthToTable = (
      table: Element,
      width: string | null | number,
      capWidth = true
    ) => {
      if (width == null || width === '') return;
      let widthStr =
        typeof width === 'number' ? `${width}px` : String(width);
      const numeric = Number(widthStr.replace('px', '').replace('%', ''));
      if (capWidth && !Number.isNaN(numeric) && numeric > MAX_EMAIL_WIDTH) {
        widthStr = `${MAX_EMAIL_WIDTH}px`;
      }
      const clean = widthStr.replace(/\s+/g, '');
      const attrWidth = clean.endsWith('px')
        ? clean.replace('px', '')
        : clean;
      table.setAttribute('width', attrWidth);
      const current = table.getAttribute('style');
      const next = current ? `${current}; width:${clean}` : `width:${clean}`;
      table.setAttribute('style', next);
    };

    const applyCenterToTable = (table: Element, style: string | null) => {
      if (!style) return;
      const hasAutoMargin =
        /margin\s*:\s*0\s*auto/i.test(style) ||
        (/margin-left\s*:\s*auto/i.test(style) &&
          /margin-right\s*:\s*auto/i.test(style));
      if (hasAutoMargin) {
        table.setAttribute('align', 'center');
        const current = table.getAttribute('style');
        const next = current
          ? `${current}; margin:0 auto`
          : 'margin:0 auto';
        table.setAttribute('style', next);
      }
    };

    doc.querySelectorAll('figure').forEach((figure) => {
      const table = figure.querySelector('table');
      const figureStyle = figure.getAttribute('style');
      const figureWidth = extractStyleValue(figureStyle, 'width');

      if (table) {
        table.setAttribute('data-email-table', '1');
        applyWidthToTable(table, figureWidth, false);
        applyCenterToTable(table, figureStyle);
      }

      const parent = figure.parentNode;
      if (!parent) return;
      while (figure.firstChild) {
        parent.insertBefore(figure.firstChild, figure);
      }
      parent.removeChild(figure);
    });

    // Keep CKEditor todo lists as-is (do not replace checkboxes)

    // Replace <mark> with <span> + inline background (better email support)
    doc.querySelectorAll('mark').forEach((mark) => {
      const span = doc.createElement('span');
      span.setAttribute('style', 'background-color: #fff2ac;');
      span.innerHTML = mark.innerHTML;
      mark.replaceWith(span);
    });

    // Leave checkbox markup as-is
    // Remove list bullets for checkbox lists in emails
    doc.querySelectorAll('ul, ol').forEach((list) => {
      if (!list.querySelector('input[type="checkbox"]')) return;
      list.setAttribute(
        'style',
        'list-style:none; padding-left:0; margin:0 0 12px 0;'
      );
      list.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        const current = checkbox.getAttribute('style');
        const extra =
          'transform:scale(1.2); transform-origin:left center; margin:0 6px 0 0; vertical-align:middle;';
        checkbox.setAttribute('style', current ? `${current}; ${extra}` : extra);
      });
      Array.from(list.children).forEach((child) => {
        if (child.tagName !== 'LI') return;
        const current = child.getAttribute('style');
        const extra = 'list-style:none; margin:0 0 8px 0; padding:0;';
        child.setAttribute('style', current ? `${current}; ${extra}` : extra);
      });
    });

    const appendStyle = (el: Element, extra: string) => {
      const current = el.getAttribute('style');
      el.setAttribute('style', current ? `${current}; ${extra}` : extra);
    };

    const wrapInCenterTable = (node: Element) => {
      const existing = node.closest('table[data-email-center="1"]');
      if (existing) return;

      const table = doc.createElement('table');
      table.setAttribute('role', 'presentation');
      table.setAttribute('cellpadding', '0');
      table.setAttribute('cellspacing', '0');
      table.setAttribute('border', '0');
      table.setAttribute('width', '100%');
      table.setAttribute('align', 'center');
      table.setAttribute('data-email-center', '1');
      table.setAttribute('style', 'width:100%; border-collapse:collapse;');

      const tr = doc.createElement('tr');
      const td = doc.createElement('td');
      td.setAttribute('align', 'center');
      td.setAttribute('style', 'text-align:center;');

      const parent = node.parentNode;
      if (!parent) return;
      parent.insertBefore(table, node);
      table.appendChild(tr);
      tr.appendChild(td);
      td.appendChild(node);
    };

    doc.querySelectorAll('table').forEach((table, index) => {
      const isEmailTable =
        table.getAttribute('data-email-table') === '1' ||
        table.classList.contains('ck-table') ||
        table.classList.contains('table');

      table.setAttribute('cellpadding', '0');
      table.setAttribute('cellspacing', '0');
      table.setAttribute('border', '0');
      // Keep authoring width if provided; do not force 100% to avoid stretching.
      appendStyle(table, 'border-collapse:collapse;');

      const styleWidth =
        extractStyleValue(table.getAttribute('style'), 'width') ||
        table.getAttribute('width');
      if (isEmailTable) {
        applyWidthToTable(table, '100%', false);
      } else if (!styleWidth) {
        const measuredWidth = measuredTableWidths[index];
        if (measuredWidth && measuredWidth > 0) {
          applyWidthToTable(table, measuredWidth);
        }
      }

      if (isEmailTable) {
        table.querySelectorAll('th, td').forEach((cell) => {
          appendStyle(
            cell,
            'border:1px solid #d9d9d9; padding:6px 8px; vertical-align:middle; font-family:Arial, Helvetica, sans-serif; font-size:16px; line-height:22px;'
          );
        });
      }
      // Center tables via email-safe wrapper (strip on load)
      if (isEmailTable) {
        table.setAttribute('align', 'center');
        wrapInCenterTable(table);
      } else {
        appendStyle(table, 'display:inline-table; margin:0 auto;');
        wrapInCenterTable(table);
      }
    });

    // Normalize images for email and center when needed
    doc.querySelectorAll('img').forEach((img, index) => {
      const style = img.getAttribute('style') || '';
      const width =
        extractStyleValue(style, 'width') || img.getAttribute('width');

      // Replace with a clean, email-safe img to avoid sanitizers dropping it
      const cleanImg = doc.createElement('img');
      const src = img.getAttribute('src');
      if (src) cleanImg.setAttribute('src', src);
      const alt = img.getAttribute('alt');
      if (alt) cleanImg.setAttribute('alt', alt);
      cleanImg.setAttribute('align', 'center');

      let appliedWidth = width || '';
      if (!appliedWidth || appliedWidth.includes('%') || appliedWidth === '0') {
        const measured = measuredImageWidths[index];
        if (measured && measured > 0) {
          appliedWidth = `${measured}px`;
        }
      }

      const numericWidth = Number(
        String(appliedWidth).replace('px', '').replace('%', '')
      );
      if (appliedWidth && !Number.isNaN(numericWidth) && numericWidth > 0) {
        cleanImg.setAttribute('width', appliedWidth.replace('px', ''));
      } else {
        cleanImg.removeAttribute('width');
      }

      appendStyle(
        cleanImg,
        'display:block; margin:0 auto; max-width:100%; height:auto; border:0; outline:none; text-decoration:none;'
      );

      const parent = img.parentElement;
      const parentStyle = parent?.getAttribute('style') || '';
      const shouldCenter =
        /text-align\s*:\s*center/i.test(parentStyle) ||
        /margin\s*:\s*0\s*auto/i.test(style) ||
        img.getAttribute('align') === 'center';

      // Replace in DOM first so centering wrapper can attach correctly
      img.replaceWith(cleanImg);

      const centerThisImage = shouldCenter || true;
      if (centerThisImage) {
        wrapInCenterTable(cleanImg);
      }
    });

    return doc.body.innerHTML;
  };

  const extractWrappedEmailContent = (html: string) => {
    if (!html) return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Remove email-specific checkbox styling when loading into editor
    doc.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.removeAttribute('style');
    });
    // Remove centering wrappers added for email rendering
    doc.querySelectorAll('table[data-email-center="1"]').forEach((wrapper) => {
      const td = wrapper.querySelector('td');
      if (!td) return;
      const parent = wrapper.parentNode;
      if (!parent) return;
      while (td.firstChild) {
        parent.insertBefore(td.firstChild, wrapper);
      }
      parent.removeChild(wrapper);
    });
    const directWrapper =
      doc.querySelector('#email-wrapper') ||
      doc.querySelector('[data-email-wrapper="1"]');
    if (directWrapper) return directWrapper.innerHTML;

    // Heuristic unwrap even if <!doctype>/<html> were stripped by backend
    // Look for outer presentation table -> inner container table -> inner td
    const isWrapperTable = (t: Element) => {
      const style = t.getAttribute('style') || '';
      return /background-color\s*:\s*#f5f5f5/i.test(style);
    };

    const outerPresentation =
      doc.querySelector('table[role="presentation"]') ||
      doc.body.querySelector('table');

    const containerTable = outerPresentation?.querySelector('table');
    const innerContainer =
      containerTable?.querySelector('table') || doc.body.querySelector('table table');
    const innerTd = innerContainer?.querySelector('td');
    if (innerTd && innerTd.innerHTML.trim()) {
      return innerTd.innerHTML;
    }

    // Generic unwrap if content is only a single wrapper table
    const firstTable = doc.body.querySelector('table');
    if (firstTable && isWrapperTable(firstTable)) {
      const td = firstTable.querySelector('td');
      if (td?.innerHTML) return td.innerHTML;
    }

    return doc.body.innerHTML;
  };

  const wrapEmailHtml = (html: string) => {
    const normalized = normalizeEmailHtml(html);
    if (!normalized) return normalized;
    if (/<!doctype|<html/i.test(normalized)) return normalized;

    return normalized;
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const showCancelConfirmModal = () => {
    modal.confirm({
      icon: (
        <InfoCircleFilled
          style={{
            color: '#FAAD14'
          }}
        />
      ),
      title: 'Bạn có chắc chắn xóa thông báo này không?',
      content: (
        <em>
          Khi hủy thông báo này thì toàn bộ dữ liệu đã tạo sẽ bị xóa.
        </em>
      ),
      okText: 'Xóa',
      cancelText: 'Hủy',
      centered: true,
      className: 'custom-modal-center-button',
      onOk: () => {
        handleGoBack();
      },
      onCancel: () => {
      }
    });
  };

  const handleSubmit = (values: any) => {
    if (!user?.id) return;

    const sendDateTime = values.sendDate && values.sendTime
      ? dayjs(values.sendDate)
          .hour(values.sendTime.hour())
          .minute(values.sendTime.minute())
          .second(0)
          .format('YYYY-MM-DD HH:mm:ss')
      : '';

    const normalizedContent = wrapEmailHtml(values.content);

    if (isEditMode && notificationId) {
      const updatePayload: UpdateEmailNotificationPayload = {
        ID: notificationId,
        TITLE: values.title,
        CONTENT: normalizedContent,
        TARGET_TYPE: values.recipients,
        TARGET_GROUP: values.targetGroup || [],
        SEND_DATE: sendDateTime,
        UPDATED_BY: user.id
      };
      
      dispatch(updateEmailNotification(updatePayload)).then(() => {
        handleGoBack();
      });
    } else {
      const createPayload: CreateEmailNotificationPayload = {
        TITLE: values.title,
        CONTENT: normalizedContent,
        TARGET_TYPE: values.recipients,
        TARGET_GROUP: values.targetGroup || [],
        SEND_DATE: sendDateTime,
        CREATED_BY: user.id
      };
      
      dispatch(createEmailNotification(createPayload)).then(() => {
        handleGoBack();
      });
    }
  };

  useEffect(() => {
    if (targetGroups.length === 0) {
      dispatch(getTargetGroups());
    }
  }, [dispatch, targetGroups.length]);

  useEffect(() => {
    if (isEditMode && notificationId) {
      dispatch(getEmailNotificationDetail(notificationId));
    }
  }, [dispatch, isEditMode, notificationId]);

  useEffect(() => {
    if (emailNotificationDetail && isEditMode) {
      const sendDate = emailNotificationDetail.SEND_DATE 
        ? dayjs(emailNotificationDetail.SEND_DATE) 
        : null;

      const editorContent = extractWrappedEmailContent(
        emailNotificationDetail.CONTENT
      );

      form.setFieldsValue({
        title: emailNotificationDetail.TITLE,
        content: editorContent,
        sendDate: sendDate,
        sendTime: sendDate,
        recipients: emailNotificationDetail.TARGET_TYPE,
        targetGroup: emailNotificationDetail.TARGET_GROUP
          ? emailNotificationDetail.TARGET_GROUP.map((group) => group.ID)
          : []
      });

      setTargetGroupValue(
        emailNotificationDetail.TARGET_GROUP?.map((group) => group.ID) || []
      );

      setEditorKey(prev => prev + 1);
    }
  }, [emailNotificationDetail, isEditMode, form]);

  useEffect(() => {
    return () => {
      form.resetFields();
      setTargetGroupValue([]);
      setEditorKey(0);
      dispatch(resetNotificationDetail());
    };
  }, [form, dispatch]);

  return (
    <div css={rootStyles}>
      <style>
        {`
          .custom-modal-center-button .ant-modal-confirm-btns {
            display: flex !important;
            justify-content: center !important;
            gap: 1rem !important;
          }

          .custom-modal-center-button .ant-btn-primary {
            margin-left: 0 !important;
          }

          .custom-modal-center-button .ant-modal-confirm-content {
            font-style: italic !important;
          }
        `}
      </style>
      <Row css={boxStyles} justify='space-between'>
        <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
          {isEditMode ? 'Cập nhật thông báo' : 'Tạo mới thông báo'} Email
        </Typography.Title>

        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        />
      </Row>

      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        css={formStyles}
      >
        {/* Thời gian gửi Section */}
        <div css={sectionStyles}>
          <Row gutter={[8, 0]} align='bottom'>
            <Col span={4}>
              {CustomLabel({ text: 'Thời gian gửi', isRequired: true })}
            </Col>
            <Col span={5}>
              <Form.Item
                name='sendDate'
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn ngày gửi thông báo'
                  },
                  {
                    validator: (_, value) => {
                      if (disableSendDateTime) {
                        return Promise.resolve();
                      }
                      if (!value) {
                        return Promise.resolve();
                      }
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');

                      if (selectedDate.isBefore(today)) {
                        return Promise.reject(
                          new Error('Ngày gửi không được là ngày trong quá khứ')
                        );
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  placeholder='Chọn ngày gửi thông báo'
                  format={'DD/MM/YYYY'}
                  style={{ width: '100%' }}
                  disabled={disableSendDateTime}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf('day');
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                style={{ marginBottom: 0 }}
                shouldUpdate={(prev, cur) => prev.sendDate !== cur.sendDate}
                dependencies={['sendDate']}
              >
                {({ getFieldValue }) => {
                  const sendDate = getFieldValue('sendDate');

                  return (
                    <Form.Item
                      name='sendTime'
                      dependencies={['sendDate']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn giờ gửi thông báo'
                        },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            if (disableSendDateTime) return Promise.resolve();
                            if (!value) return Promise.resolve();

                            const sendDate = getFieldValue('sendDate');
                            if (!sendDate) return Promise.resolve();

                            const today = dayjs().startOf('day');
                            const selectedDate = dayjs(sendDate).startOf('day');

                            if (selectedDate.isSame(today)) {
                              const currentTime = dayjs();
                              const selectedDateTime = dayjs(sendDate)
                                .hour(value.hour())
                                .minute(value.minute());
                              if (selectedDateTime.isBefore(currentTime)) {
                                return Promise.reject(
                                  new Error('Giờ gửi không được là giờ trong quá khứ')
                                );
                              }
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <DatePicker.TimePicker
                        key={sendDate ? sendDate.format('YYYY-MM-DD') : 'default'}
                        placeholder='Chọn giờ gửi thông báo'
                        format='HH:mm'
                        style={{ width: '100%' }}
                        disabled={disableSendDateTime}
                        disabledTime={() => {
                          if (!sendDate) return {};

                          const today = dayjs().startOf('day');
                          const selectedDate = dayjs(sendDate).startOf('day');

                          if (selectedDate.isSame(today)) {
                            const currentHour = dayjs().hour();
                            const currentMinute = dayjs().minute();

                            return {
                              disabledHours: () =>
                                Array.from({ length: currentHour }, (_, i) => i),
                              disabledMinutes: (hour: number) =>
                                hour === currentHour
                                  ? Array.from({ length: currentMinute + 1 }, (_, i) => i)
                                  : []
                            };
                          }

                          return {};
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Nội dung thông báo Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
            Nội dung thông báo
          </Typography.Title>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Tiêu đề email', isRequired: true })}
            </Col>
            <Col span={10}>
              <Form.Item
                name='title'
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder='Nhập tiêu đề email'
                  style={{ padding: '8px 12px', width: '95%' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Nội dung email', isRequired: true })}
            </Col>
            <Col span={24}>
              <Form.Item
                name='content'
                rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
              >
                <div css={emailCKEditorStyles}>
                  <CKEditorMaster
                    key={`email-editor-${editorKey}`}
                    content={form.getFieldValue('content') || null}
                    placeholder='Nhập nội dung email'
                    handleUpdateContent={(data) => {
                      form.setFieldsValue({ content: data });
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Danh sách người nhận Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
            Danh sách người nhận
          </Typography.Title>

          <Row gutter={16}>
            <Col span={3}>
              {CustomLabel({ text: 'Người nhận', isRequired: true })}
            </Col>
            <Col span={6}>
              <Form.Item
                name='recipients'
                rules={[
                  { required: true, message: 'Vui lòng chọn người nhận' }
                ]}
                initialValue={'ALL'}
              >
                <Select defaultActiveFirstOption options={receiverOptions} allowClear style={{ width: '60%' }}/>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const receiverValue = getFieldValue('recipients');

              if (receiverValue === 'ALL') {
                return (
                  <div>
                    <CaretRightOutlined /> Khớp{' '}
                    <strong>tất cả người dùng</strong>
                  </div>
                );
              }

              return (
                <Form.Item
                  name='targetGroup'
                  label='Nhập các điều kiện để chọn đối tượng gửi thông báo'
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 6 }}
                >
                  <div
                    css={
                      targetGroupValue.length > 0
                        ? null
                        : ContainerSelectHealthStatus
                    }
                  >
                    <Select
                      css={selectStyleHealthStatus}
                      mode='multiple'
                      allowClear
                      options={targetGroupOptions}
                      placeholder='Chọn Trạng thái sức khỏe'
                      style={{ width: '95%' }}
                      value={targetGroupValue}
                      onChange={(value) => {
                        setTargetGroupValue(value || []);
                        form.setFieldValue('targetGroup', value || []);
                      }}
                      removeIcon={
                        <CloseCircleOutlined
                          style={{ color: '#1890FF', fontSize: '1.9rem' }}
                        />
                      }
                    />
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        {/* Submit Buttons */}
        <div css={submitButtonsStyles}>
          <Button
            type='default'
            onClick={isEditMode ? handleGoBack : showCancelConfirmModal}
            style={{
              width: '25%'
            }}
          >
            Hủy
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading || loadingNotification}
            style={{
              width: '25%'
            }}
          >
            {isEditMode ? 'Cập nhật' : 'Lưu'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const sectionStyles = css`
  padding: 0.4rem 0.8rem;
  background: var(--white-color);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const submitButtonsStyles = css`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1.6rem 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
`;

const ContainerSelectHealthStatus = css`
  border: 1px solid #00000040;
  border-radius: 0.5rem;
  padding: 0.8rem 1.6rem;

  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
    }
  }
`;

const selectStyleHealthStatus = css`
  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
      font-size: 1.2rem !important;
    }
  }
`;

const emailCKEditorStyles = css`
  margin-top: 0.8rem;
  ul li,
  ol li,
  nav ul {
    list-style: revert !important;
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    padding: 0.5rem 2.8rem;
  }
`;


